import { ApiError, ValidationApiError } from "@anna-money/anna-web-lib/api";
import type { FlowTypeFormValues } from "helpers/forms/acn/flowTypes";
import type { DirectorStepFormValues } from "helpers/forms/au/directorTypes";
import type { IdentiyFormValues } from "helpers/forms/au/identityTypes";
import type { ConsentFormValues } from "helpers/forms/consentTypes";
import { extractBackendValidationError } from "helpers/forms/errors";
import { getUrlParam } from "helpers/url";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import type { BillingControllerStore } from "services/billingController/billingControllerStore";
import type { BusinessActivitiesStore } from "services/businessActivities/businessActivitiesStore";
import { type CompanyStore } from "services/company/companyStore";
import type { CompanyBusinessActivitiesStore } from "services/companyBusinessActivities/au/companyBusinessActivitiesStore";
import type { ConsentStore } from "services/consent/consentStore";
import type { CountryStore } from "services/country/countryStore";
import { type DirectorAddressStore } from "services/director/directorAddressStore";
import { type DirectorEntity, type DirectorStore } from "services/director/directorStore";
import type { FeatureStore } from "services/feature/featureStore";
import { type IncorporationStore } from "services/incorporation/incorporationStore";
import { type IssuesStore } from "services/issues/issuesStore";
import { type PaymentMethodsStore } from "services/paymentMethods/paymentMethodsStore";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";
import { type SoleTraderDecisionQuestionsStore } from "services/questions/au/soleTraderDecisionQuestionsStore";
import { RegisterFormStoreBase } from "services/registerForm/registerFormStoreBase";
import { type CompanyAbnStepStore } from "services/stepStore/au/abn/companyAbnStepStore";
import { IdentityStepEntity, type IdentityStepStore } from "services/stepStore/au/identityStepStore";
import {
  type AbnSoleTraderFormValues,
  AbnSoleTraderStepEntity,
  type AbnSoleTraderStepStore,
} from "services/stepStore/au/soleTrader/abnSoleTraderStepStore";
import type { SumsubApplicationStore } from "services/sumsub/sumsubApplicationStore";
import { flowTypeUrlParam, parseFlowTypeUrlParam } from "services/user/flowTypeUrlParam";
import type { UserStore } from "services/user/userStore";
import { FlowType } from "services/user/userTypes";
import { FormError } from "types/errors";
import { type FormSubmitResult, FormValidationError } from "types/form";
import { type AbnSoleTraderRegisterFormStepsStore } from "./abnSoleTraderRegisterFormStepsStore";
import { SoleTraderFormStep } from "./constants";

export class AbnSoleTraderRegisterFormStore extends RegisterFormStoreBase<SoleTraderFormStep> {
  constructor(
    private readonly _directorStore: DirectorStore,
    private readonly _companyStore: CompanyStore,
    private readonly _paymentMethodsStore: PaymentMethodsStore,
    private readonly _consentStore: ConsentStore,
    private readonly _companyBusinessActivitiesStore: CompanyBusinessActivitiesStore,
    private readonly _businessActivitiesStore: BusinessActivitiesStore,
    protected readonly _userStore: UserStore,
    protected readonly _sumsubApplicationStore: SumsubApplicationStore,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _registerFormStepsStore: AbnSoleTraderRegisterFormStepsStore,
    protected readonly _incorporationStore: IncorporationStore,
    protected readonly _companyIssuesStore: IssuesStore,
    protected readonly _companyStepStore: CompanyAbnStepStore,
    protected readonly _identityStepStore: IdentityStepStore,
    protected readonly _countryStore: CountryStore,
    private readonly _soleTraderDecisionQuestionsStore: SoleTraderDecisionQuestionsStore,
    private readonly _directorAddressStore: DirectorAddressStore,
    protected readonly _abnSoleTraderStepStore: AbnSoleTraderStepStore,
    private readonly _featureStore: FeatureStore,
    private readonly _billingControllerStore: BillingControllerStore,
  ) {
    super(_registerFormStepsStore, _userStore, _incorporationStore, _companyIssuesStore, _analyticsManager);
  }

  protected async _init(): Promise<void> {
    this._analyticsManager.event("sole-trader-form-init-started");

    await this._companyStore.init();

    await Promise.all([
      this._consentStore.init(),
      this._directorStore.init(),
      this._sumsubApplicationStore.init(),
      this._companyIssuesStore.init(),
      this._incorporationStore.init(),
      this._paymentMethodsStore.init(),
      this._companyBusinessActivitiesStore.init(),
      this._businessActivitiesStore.init(),
      this._countryStore.init(),
    ]);

    // Initialize sole trader decision from company data
    const company = this._companyStore.company;
    if (company?.soleTraderDecision) {
      this._soleTraderDecisionQuestionsStore.initializeFromData(company.soleTraderDecision);
    }

    if (this._featureStore.hasFeature("formationPaymentStatus")) {
      await this._billingControllerStore.fetchPaymentStatus();

      this._analyticsManager.event("sole-trader-form-payment-status-checked", {
        isPaymentCompleted: this._billingControllerStore.isPaymentCompleted,
      });
    }

    await this._tryApplyFlowTypeFromUrl();

    this._analyticsManager.event("sole-trader-form-init-completed");
  }

  protected _getRelevantStep(): SoleTraderFormStep {
    // Show flow-select if flowType is not set yet
    if (!this._userStore.flowType) {
      return SoleTraderFormStep.FLOW_SELECT;
    }

    // Check if consent step is needed
    if (!this._consentStore.hasAuConsents) {
      return SoleTraderFormStep.CONSENT;
    }

    // Check if sole trader decision flow is complete
    if (!this._companyStore.isSoleTraderDecisionComplete()) {
      return SoleTraderFormStep.SOLE_TRADER_DECISION;
    }

    // Check if director step is complete
    const { mainDirector } = this._directorStore;
    if (!mainDirector?.firstName || !mainDirector?.lastName) {
      return SoleTraderFormStep.DIRECTOR;
    }
    if (!mainDirector.dob) {
      return SoleTraderFormStep.DIRECTOR;
    }
    const address = this._directorAddressStore.tryGetForDirector(mainDirector.id);
    if (!address) {
      return SoleTraderFormStep.DIRECTOR;
    }

    if (!mainDirector.tfn) {
      return SoleTraderFormStep.DIRECTOR;
    }

    // Check if ABN step is complete
    if (!this._abnSoleTraderStepStore.checkAllAbnDetailsExist()) {
      return SoleTraderFormStep.ABN;
    }

    // Check if identity step is complete
    if (this._sumsubApplicationStore.sumsubApplication) {
      return SoleTraderFormStep.IDENTITY;
    }

    // If payment already completed, skip CHARGE — go directly to COMPLETE
    if (this._featureStore.hasFeature("formationPaymentStatus") && this._billingControllerStore.isPaymentCompleted) {
      this._analyticsManager.event("sole-trader-form-payment-already-completed-skip-charge");
      return SoleTraderFormStep.COMPLETE;
    }

    return SoleTraderFormStep.CONFIRM;
  }

  async goBack(): Promise<void> {
    await super.goBack();
  }

  protected async _updateStepData(data: unknown, entity?: unknown): Promise<FormSubmitResult> {
    try {
      switch (this._registerFormStepsStore.activeStep.type) {
        case SoleTraderFormStep.FLOW_SELECT: {
          const { flowType = FlowType.AcnAbn } = data as FlowTypeFormValues;
          await this._applyFlowSelection(flowType);
          break;
        }
        case SoleTraderFormStep.CONSENT:
          await this._consentStore.updateConsents(data as ConsentFormValues);
          break;
        case SoleTraderFormStep.SOLE_TRADER_DECISION: {
          const { answer } = data as { answer: string };
          const { isFlowComplete } = this._soleTraderDecisionQuestionsStore.processAnswer(answer);

          // Save the answer trail to company data
          const answerTrailData = this._soleTraderDecisionQuestionsStore.getAnswerTrailForApi();
          await this._companyStore.updateSoleTraderDecision(answerTrailData);

          if (isFlowComplete) {
            // Flow is complete, will advance to next step
            break;
          }
          // Flow is not complete, stay on the same step but show next question
          // Return empty result to prevent advancing to next step
          return undefined;
        }
        case SoleTraderFormStep.DIRECTOR: {
          await this._directorStore.updateMainDirector(data as DirectorStepFormValues, entity as DirectorEntity);
          break;
        }
        case SoleTraderFormStep.ABN: {
          const { continueFlow } = await this._abnSoleTraderStepStore.updateEntity(
            data as AbnSoleTraderFormValues,
            entity as AbnSoleTraderStepEntity,
          );

          // If flow is not complete, stay on ABN step (show next question)
          if (!continueFlow) {
            return undefined;
          }
          break;
        }
        case SoleTraderFormStep.IDENTITY:
          await this._identityStepStore.updateEntity(data as IdentiyFormValues, entity as IdentityStepEntity);
          break;
        case SoleTraderFormStep.BASKET:
          // Basket step just collects payment method, no data to update
          break;
        case SoleTraderFormStep.CHARGE: {
          await this._createIncorporation();
          break;
        }
        case SoleTraderFormStep.CONFIRM: {
          // Confirm step is now after Charge, may not need any data update
          break;
        }
      }
    } catch (error) {
      if (error instanceof ValidationApiError) {
        return extractBackendValidationError(error);
      }
      if (error instanceof ApiError || error instanceof FormError) {
        return new FormValidationError(error.message);
      }
      throw error;
    }
  }

  private async _applyFlowSelection(flowType: FlowType): Promise<void> {
    await this._userStore.updateCurrentFlow(flowType);
    if (!this._companyStore.company) {
      await this._companyStore.createCompany();
    }
    await this._updatePricingPlanFromFlowType(flowType);
  }

  private async _tryApplyFlowTypeFromUrl(): Promise<void> {
    const flowType = parseFlowTypeUrlParam(getUrlParam(flowTypeUrlParam));

    if (!flowType || this._userStore.flowType) {
      return;
    }

    await this._applyFlowSelection(flowType);

    this._analyticsManager.event("flow-type-applied-from-url", { flowType });
  }

  private async _updatePricingPlanFromFlowType(flowType: FlowType): Promise<void> {
    let pricingPlan: PricingPlan;

    switch (flowType) {
      case FlowType.SoleTraderAbn:
        pricingPlan = PricingPlan.SoleTraderLaunch;
        break;
      case FlowType.Abn:
      case FlowType.AcnAbn:
        pricingPlan = PricingPlan.EasyCompany;
        break;
      default:
        this._analyticsManager.event("flow-type-pricing-plan-mapping-error", {
          flowType,
          reason: "unknown_flow_type",
        });
        throw new Error(`Unknown flow type for pricing plan mapping: ${String(flowType)}`);
    }

    try {
      await this._companyStore.updatePricingPlan(pricingPlan);

      this._analyticsManager.event("pricing-plan-updated-from-flow-selection", {
        flowType,
        pricingPlan,
      });
    } catch (error) {
      this._analyticsManager.event("pricing-plan-update-error", {
        flowType,
        pricingPlan,
        error: (error as Error).message,
      });
      throw error;
    }
  }
}
